import {Card} from 'platform/components';
import {Box, Center, Icon, Text, VStack} from 'platform/foundation';
import styled, {keyframes} from 'styled-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';

interface ImportResultLoadingViewProps extends TestIdProps {
  importName?: string;
}

export function ImportResultLoadingView(props: ImportResultLoadingViewProps) {
  const importName = props.importName ?? 'import';

  return (
    <Card data-testid={suffixTestId('card', props)}>
      <Box padding={6}>
        <Center>
          <VStack spacing={2} align="center">
            <IconOrbitalWrapper>
              <OrbitalRingOuter />
              <OrbitalRingMiddle />
              <OrbitalRingInner />
              <OrbitalCenter>
                <Icon
                  value="editor/insert_drive_file"
                  size={6}
                  color="general.white"
                  data-testid={suffixTestId('headerIcon', props)}
                />
              </OrbitalCenter>
            </IconOrbitalWrapper>
            <Text size="base" alternative>
              {i18n.t('page.import.processing.title')}
            </Text>
            <Text size="small" color="secondary">
              {importName} — {i18n.t('page.import.processing.detectingVehicles')}
            </Text>
          </VStack>
        </Center>
      </Box>
    </Card>
  );
}

const orbit = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const IconOrbitalWrapper = styled.div`
  position: relative;
  width: ${({theme}) => theme.getSize(24)};
  height: ${({theme}) => theme.getSize(24)};
`;

const OrbitalRingBase = styled.div`
  position: absolute;
  border-radius: 50%;
  border-style: solid;
  border-width: ${({theme}) => theme.getSize(1)};
  border-color: transparent;
  opacity: 0.95;
`;

const OrbitalRingOuter = styled(OrbitalRingBase)`
  inset: 0;
  border-top-color: ${({theme}) => theme.colors.palettes.blue[50][100]};
  animation: ${orbit} 3000ms linear infinite;
`;

const OrbitalRingMiddle = styled(OrbitalRingBase)`
  top: ${({theme}) => theme.getSize(2)};
  left: ${({theme}) => theme.getSize(2)};
  width: calc(100% - ${({theme}) => theme.getSize(4)});
  height: calc(100% - ${({theme}) => theme.getSize(4)});
  border-top-color: ${({theme}) => theme.colors.palettes.blue[40][100]};
  animation: ${orbit} 2200ms linear infinite reverse;
`;

const OrbitalRingInner = styled(OrbitalRingBase)`
  top: ${({theme}) => theme.getSize(4)};
  left: ${({theme}) => theme.getSize(4)};
  width: calc(100% - ${({theme}) => theme.getSize(8)});
  height: calc(100% - ${({theme}) => theme.getSize(8)});
  border-top-color: ${({theme}) => theme.colors.palettes.blue[30][100]};
  animation: ${orbit} 1600ms linear infinite;
`;

const OrbitalCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({theme}) => theme.getSize(10)};
  height: ${({theme}) => theme.getSize(10)};
  border-radius: ${({theme}) => theme.getSize(2)};
  background: ${({theme}) => theme.colors.palettes?.blue?.[50]?.[100] ?? '#6366f1'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(3)}
    rgba(79, 70, 229, 0.3);
  z-index: 1;
`;
